import React, { useState, useEffect, useContext } from "react";
import {
    Button,
    CloseIcon,
    ComponentEventHandler,
    Divider,
    Flex,
    Header,
    MenuIcon,
    MenuProps,
    Text,
} from "@fluentui/react-northstar";
import TasksContainer from "tasksBoard/TasksContainer";
import { CardEnum } from "./CardEnum";
import pl from "shared/messages/pl";
import Tabs from "tabs/Tabs";
import styles from "./appContainer.module.scss";
import MotivationCard from "motivationCard/MotivationCard";
import GoalsCard from "goalsCard/GoalsCard";
import PowerBiReportsTab from "powerbi-reports/PowerBiReportsTab";
import { useNavigate } from "react-router-dom";
import pages from "shared/routing/pages";
import { Tab } from "shared/models/Tab";
import { FeedbackDialog } from "modules/feedback";
import View360Card from "view360Card/View360Card";
import { trackPageView } from "appilication-insights/ApplicationInsightsUtils";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { AuthContext } from "authorization/AuthProvider";
import EmptyContent from "view360Card/components/emptyContent/EmptyContent";
import { tabDefinitions } from "shared/constants/tabDefinitions";
import AdminAuthorizationService from "authorization/service/AdminAuthorizationService";
import { ResizerContext } from "shared/stores/resizeStore/ResizerContext";
import { constants } from "shared/constants";
import ViewGlobalNotes from "view360Card/features/notes/components/ViewGlobalNotes";

export interface AppContainerProps {
    firstLevelTab: CardEnum | undefined;
    secondLevelTab?: number;
}

const AppContainer: React.FC<AppContainerProps> = (props: AppContainerProps) => {
    const resizeContext = useContext(ResizerContext);
    const [activeTabKey, setActiveTabKey] = useState(props.firstLevelTab);
    const [tabs, setTabs] = useState<Tab[]>();
    const appInsightsContext = useAppInsightsContext();
    const authContext = useContext(AuthContext);
    const [isInitialized, setIsInitialized] = useState(false);
    const [mobileMenuOpened, setMobileMenuOpened] = useState(false);

    const isMobile = resizeContext.windowWidth < constants.view360MobileDeviceWidthPx;

    const navigation = useNavigate();

    useEffect(() => {
        const initialize = async () => {
            const availableTabs : Tab[] = [];
            tabDefinitions.main.forEach(tabDef => {
                if (authContext.isUserGrantedTo(tabDef.requiredgrant)) {
                    availableTabs.push(tabDef);
                }
            });
            setTabs(availableTabs);
            setIsInitialized(true);
        };
        initialize();
    }, []);

    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        if (props.firstLevelTab === undefined) {
            if (tabs && tabs.length > 0) {
                const homePage = getRouteForTab(0);
                navigation(homePage);
            }
            return;
        }

        const requestedTabAvailable = (tabs ?? []).findIndex(t => t.key === props.firstLevelTab) >= 0;
        if (requestedTabAvailable) {
            setActiveTabKey(props.firstLevelTab);
        }

    }, [props.firstLevelTab, isInitialized]);

    const getRouteForTab = (tabIndex: number): string => {
        let selectedTab = undefined;
        if (tabs && tabs.length > tabIndex) {
            selectedTab = tabs[tabIndex].key;
        }

        switch (selectedTab) {
            case CardEnum.GoalsCard:
                return pages.goalsMonthly.path;
            case CardEnum.MotivationCard:
                return pages.motivationMonthly.path;
            case CardEnum.Tasks:
                return pages.tasks.path;
            case CardEnum.PowerBiReports:
                return pages.powerBiReports.path;
            case CardEnum.View360:
                return pages.view360.path;
            case CardEnum.GlobalNotes:
                return pages.globalNotes.path;
            default:
                return pages.root.path;
        }
    };

    const handleOnActiveIndexChange: ComponentEventHandler<MenuProps> = async (tmp, data) => {
        if (data?.activeIndex === undefined) {
            return;
        }

        if (data?.activeIndex && data?.items) {
            const rmsPortalTabIndex = data.items.findIndex((x) => (x as Tab)?.key === CardEnum.RmsPortal);
            const userProfileAdmIndex = data.items.findIndex((x) => (x as Tab)?.key === CardEnum.UserProfile);

            if (rmsPortalTabIndex === data.activeIndex) {
                trackPageView(appInsightsContext, "EHTA", pages.rmsPortal.path);
                window.open(process.env.REACT_APP_SHAREPOINT_PAGE_URL);
                return;
            }

            if (userProfileAdmIndex === data.activeIndex) {
                trackPageView(appInsightsContext, "EHTA", pages.userProfileAdministration.path);
                const response = await AdminAuthorizationService.getOnetimeAuthToken();
                const redirectUrl = response?.data?.redirectUrl;
                if(redirectUrl){
                    window.open(redirectUrl);
                } else {
                    alert(pl.authProvider.adminAuth.authError);
                }
                return;
            }
        }

        navigation(getRouteForTab(Number(data.activeIndex)));
        setMobileMenuOpened(false);
    };

    const getActiveTab = (): number => {
        const activeTab = tabs!.findIndex((t) => t.key === activeTabKey);
        return activeTab;
    };

    const renderActiveTab = () => {

        if ((tabs ?? []).length === 0) {
            return <EmptyContent message={pl.common.noTabsUnavailable} graphic="welcome" />
        }

        if ((tabs ?? []).findIndex(t => t.key === activeTabKey) < 0) {
            return <EmptyContent message={pl.common.tabUnavailable} graphic="welcome" />
        }

        switch (activeTabKey) {
            case CardEnum.GoalsCard:
                return <GoalsCard activeTab={props.secondLevelTab} />;

            case CardEnum.Tasks:
                return <TasksContainer />;

            case CardEnum.MotivationCard:
                return <MotivationCard activeTab={props.secondLevelTab} />;

            case CardEnum.PowerBiReports:
                return <PowerBiReportsTab />;

            case CardEnum.GlobalNotes:
                return <ViewGlobalNotes />;

            case CardEnum.View360:
                return <View360Card activeTab={props.secondLevelTab} />;
        }
    };

    if (!isInitialized) {
        return null;
    }

    const renderMobileMenu = () => {
        return (
            <div className={mobileMenuOpened ? styles.modal : ""} onClick={() => setMobileMenuOpened(false)}>
                <Flex
                    column
                    className={mobileMenuOpened ? styles.modalContentShow : styles.modalContent}
                    styles={({ theme: { siteVariables } }) => ({
                        backgroundColor: siteVariables.colorScheme.default.background,
                    })}>
                    <Flex>
                        <Flex.Item push>
                            <Button iconOnly={true} icon={<CloseIcon size="large" />} onClick={() => setMobileMenuOpened(false)} />
                        </Flex.Item>
                    </Flex>
                    <Tabs
                            activeTab={getActiveTab()}
                            activeIndex={getActiveTab()}
                            items={tabs!}
                            vertical
                            onActiveIndexChange={handleOnActiveIndexChange}
                        />
                </Flex>
            </div>
        )
    }

    // temp solution till receiving the holiday flag from BE
    const isHolidaySeason = () => {
        let today = new Date();
        let startDate = new Date("2022-12-06");
        let deadline = new Date("2023-01-06");
        return today <= deadline && today >= startDate;
    }

    return (
        <Flex column style={{ minHeight: "100vh" }}>
            <Flex space="between" vAlign="center">
                <Flex gap="gap.medium" vAlign="center" className={styles.headerWrapper}>
                    {isHolidaySeason() && <img
                        src="/assets/christmass_hat.png"
                        alt=""
                        className={isMobile ? styles.imageChristmassMobile : styles.imageChristmass}
                    />}
                    <Header className={styles.appNameHeader} as="h2">
                        {pl.common.appName}
                    </Header>
                    {!isMobile && tabs && tabs.length > 0 &&
                        (<Tabs
                            activeTab={getActiveTab()}
                            activeIndex={getActiveTab()}
                            items={tabs}
                            onActiveIndexChange={handleOnActiveIndexChange}
                            additionalClassName={styles.mobileTabs}
                        />)}
                    {isMobile && tabs && tabs.length > 0 &&
                        (<Button icon={<MenuIcon size="large" />} text iconOnly title="App Menu" onClick={() => setMobileMenuOpened(true)} />
                    )}
                    {isMobile && (
                        <Text
                            size="medium"
                            content={tabDefinitions.main.find((c) => c.key === activeTabKey)?.content}
                        />
                    )}
                </Flex>
                <FeedbackDialog />
            </Flex>
            <Divider style={{ padding: "0" }} />
            {renderActiveTab()}
            {renderMobileMenu()}
        </Flex>
    );
};

export default AppContainer;
